import React from 'react';
import { Shield, TrendingUp, Leaf, Award, Home, Car } from 'lucide-react';
import { propertyData, brandAssets, sectionImages } from '../../data/mock';

const OverviewSection = () => {
  const highlights = [
    {
      icon: Shield,
      title: "Gated Privacy",
      description: "Secure gated entrance with complete separation from neighbors"
    },
    {
      icon: TrendingUp,
      title: "Agricultural Tax",
      description: "Ag-zoned property delivers meaningful annual tax benefits"
    },
    {
      icon: Leaf,
      title: "20 Private Acres",
      description: "Rolling pastures with established infrastructure"
    },
    {
      icon: Award,
      title: "Zero HOA",
      description: "Complete freedom without restrictive covenants"
    },
    {
      icon: Home,
      title: "Multi-Residence",
      description: "Build up to 4 residences (subject to approvals)"
    },
    {
      icon: Car,
      title: "Metro Access",
      description: "~45 min to Orlando while maintaining privacy"
    }
  ];

  return (
    <section id="overview" className="py-24 bg-gradient-to-b from-[#0d1117] to-[#161b22]" data-testid="overview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Estate Overview
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-6">
            THE INFRASTRUCTURE EXISTS. THE NEXT OWNER DEFINES THE VISION.
          </h2>
          <p className="text-lg text-white/70 font-light leading-relaxed">
            {propertyData.positioning}
          </p>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {highlights.map((item) => (
            <div
              key={item.title}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 p-8 hover:border-amber-500/40 transition-all duration-500"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl group-hover:bg-amber-500/10 transition-all duration-500" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/20 border border-amber-500/30 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="w-7 h-7 text-amber-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-white/60 font-light leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Value Stack Preview */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-transparent to-amber-500/10 rounded-3xl blur-3xl" />
          <div className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0d1117] border border-amber-500/20 rounded-3xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-serif text-white mb-4">
                  The Complete Value Stack
                </h3>
                <p className="text-white/70 font-light leading-relaxed mb-6">
                  Every asset category — already built, already delivered, included at one price. 
                  Land, residence, infrastructure, wellness amenities, and expansion rights.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {propertyData.valueStack.slice(0, 6).map((item) => (
                    <div key={item.category} className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                      <p className="text-amber-400 text-sm font-medium">{item.category}</p>
                      <p className="text-white/50 text-xs mt-1">{item.data}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <img
                  src={sectionImages.birdsEye1}
                  alt="Estate aerial view"
                  className="rounded-2xl shadow-2xl w-full h-64 lg:h-80 object-cover"
                />
                <div className="absolute -bottom-4 -right-4 bg-amber-500 text-white rounded-xl px-6 py-3 shadow-xl">
                  <p className="text-sm font-medium">TURNKEY</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OverviewSection;
